import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { entrarComoDemo } from '../slices/authSlice';

/**
 * Pantalla puente del demo público: entra y manda al tablero.
 *
 * 8 oct 2026 (Augusto: "necesito un demo... para que se coloque en su
 * website", y después "el problema es que este demo no muestra
 * exactamente como se ve en PCM").
 *
 * La primera versión fueron maquetas dibujadas a mano. Se parecían a PCM
 * sin serlo, y además iban a envejecer solas: cada cambio real en la
 * aplicación las dejaba mintiendo sin que nadie se enterara.
 *
 * Esto no puede desactualizarse porque no es una copia. El visitante
 * entra a PCM de verdad, con la cuenta de ejemplo, en modo solo lectura
 * (ver POST /auth/demo y exigir_escritura en el backend).
 *
 * Existe como ruta propia --y no como un botón en Login-- para poder
 * enlazarla desde el sitio web, un correo o un WhatsApp con una sola URL:
 * pcm.darwinoptima.com/demo-en-vivo
 */
const EntrarDemo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  // React 18 monta dos veces en desarrollo; sin esto se pide la sesión
  // dos veces y la segunda pisa a la primera.
  const yaIntento = useRef(false);

  useEffect(() => {
    if (yaIntento.current) return;
    yaIntento.current = true;
    dispatch(entrarComoDemo())
      .unwrap()
      .then(() => navigate('/dashboard', { replace: true }))
      .catch((err) => {
        setError(
          err?.detail ||
            'La demostración no está disponible en este momento. Vuelve a intentarlo en un rato.'
        );
      });
  }, [dispatch, navigate]);

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        textAlign: 'center',
      }}
    >
      {error ? (
        <>
          <Typography variant="h6">No se pudo abrir la demostración</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/register')} sx={{ mt: 1 }}>
            Crear una cuenta
          </Button>
        </>
      ) : (
        <>
          <CircularProgress sx={{ color: '#B8860B' }} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Preparando la demostración...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            Vas a entrar a PCM con una cuenta de ejemplo: un año de movimientos, cuentas,
            tarjetas y presupuestos ya cargados.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default EntrarDemo;
